import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, defer, iif, Observable } from 'rxjs';
import { PrintCardDomainModel } from '../../../../domain/print-card-domain-model';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { RenderContentViewModel } from '../../base/render-content-view-model';
import { SectionRowViewModelUtils } from '../../../../utils/section-row-view-model-utils';
import { exists } from '../../../../functions/exists';
import { PrintCardMenu } from '../../../../models/menu/print-card/print-card-menu';
import type { CardData } from '../../../../models/print-cards/card-data';
import { ShelfTalkerCardData } from '../../../../models/shelf-talkers/shelf-talker-card-data';
import { ShelfTalkerUtils } from '../../../../utils/shelf-talker-utils';
import { VariantAssetService } from '../../../services/variant-asset-service';

type CardPages = {
  layoutInstructions: any;
  cardPages: CardData[][] | ShelfTalkerCardData[][];
};

type ShelfTalkerPages = CardPages & {
  cardPages: ShelfTalkerCardData[][];
};

@Injectable()
export class RenderCardsViewModel extends RenderContentViewModel {
  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    printCardDomainModel: PrintCardDomainModel,
    variantAssetService: VariantAssetService
  ) {
    super(router, activatedRoute, printCardDomainModel, variantAssetService);
  }

  private readonly layoutInstructions$ = this.printCardDomainModel.layoutInstructions$;

  private readonly cardStackCardGroupingsPerPage$: Observable<CardPages[]> = defer(() => {
    return combineLatest([
      this.layoutInstructions$,
      this.printCardMenu$,
      this.visibleOverrideVariantIdsWithVisibleSiblingIds$,
      this.companyConfig$,
      this.locationConfig$,
      this.variantCardCountMap$
    ]).pipe(
      map(([layoutInstructions, menu, visibleOverrideAndSiblingIds, compConfig, locConfig, variantCardCountMap]) => {
        return layoutInstructions?.map(stack => {
          const paperSize = menu?.displaySize;
          const onPerforatedPaper = stack?.isOnPerforatedPaper() ?? false;
          const generateCardData = SectionRowViewModelUtils.generateRowViewModels;
          const products = stack?.products;
          const vms = generateCardData(stack, menu, compConfig, locConfig, products, [], visibleOverrideAndSiblingIds);
          const cards: CardData[] = [];
          const cardsPerPage = !stack?.singleCardPerPage
            ? (stack?.nCardsPerPage(paperSize, onPerforatedPaper) ?? 1)
            : 1;
          vms?.forEach(card => {
            const key = card?.getVariantIdsSeperatedBy('-');
            const nTimes = exists(variantCardCountMap?.[key]) ? Number(variantCardCountMap?.[key]) : 1;
            for (let i = 0; i < nTimes; i++) cards.push(card as CardData);
          });
          return {
            layoutInstructions: stack,
            cardPages: menu?.cardGroupingsForEachPage(cards, cardsPerPage, onPerforatedPaper) ?? []
          };
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  });

  private readonly shelfTalkerCardGroupingsPerPage$: Observable<ShelfTalkerPages[]> = defer(() => {
    return combineLatest([
      this.layoutInstructions$,
      this.shelfTalkerMenu$,
      this.shelfTalkerCards$,
      this.overrideSectionIds$,
      this.companyConfig$,
      this.locationConfig$
    ]).pipe(
      map(
        ([
          layoutInstructions,
          shelfTalkerMenu,
          shelfTalkerCards,
          overrideSectionIds,
          companyConfig,
          locationConfig
        ]) => {
          return layoutInstructions?.map(stack => {
            const paperSize = shelfTalkerMenu?.displaySize;
            const onPerforatedPaper = shelfTalkerCards?.firstOrNull()?.isOnPerforatedPaper() ?? false;
            const builder = ShelfTalkerUtils.generateShelfTalkerCardData;
            const cardData = builder(shelfTalkerMenu, companyConfig, locationConfig, overrideSectionIds);
            const firstCard = shelfTalkerCards?.firstOrNull();
            const cardsPerPage = firstCard?.nCardsPerPage(paperSize, onPerforatedPaper) ?? 1;
            return {
              layoutInstructions: stack,
              cardPages: shelfTalkerMenu?.cardGroupingsForEachPage(cardData, cardsPerPage, onPerforatedPaper) ?? []
            };
          });
        }
      )
    );
  });

  public readonly cardAsDataGroupedPerPage$ = this.laysOutCardsOnToPaperMenu$.pipe(
    filter(laysOutCardsOnToPaperMenu => exists(laysOutCardsOnToPaperMenu)),
    switchMap(laysOutCardsOnToPaperMenu => {
      return iif(
        () => laysOutCardsOnToPaperMenu instanceof PrintCardMenu,
        this.cardStackCardGroupingsPerPage$,
        this.shelfTalkerCardGroupingsPerPage$
      );
    })
  );
}
